import { useState, useCallback } from 'react';
import { DEFAULT_USER_PREFERENCES } from '@deckspace/types';
import type { UserPreferences } from '@deckspace/types';

const STORAGE_KEY = 'deckspace-user-preferences';
const LEGACY_SCHEME_KEY = 'deckspace-color-scheme';

export interface UseUserPreferencesReturn {
  preferences: Required<UserPreferences>;
  loading: boolean;
  updatePreferences: (patch: Partial<UserPreferences>) => void;
}

function readLocalPrefs(): Required<UserPreferences> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<UserPreferences>;
      return { ...DEFAULT_USER_PREFERENCES, ...parsed };
    }

    const legacyScheme = localStorage.getItem(LEGACY_SCHEME_KEY);
    if (legacyScheme === 'dark' || legacyScheme === 'light') {
      const migrated: Required<UserPreferences> = {
        ...DEFAULT_USER_PREFERENCES,
        colorScheme: legacyScheme,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }
  } catch {
    // localStorage unavailable or corrupt
  }

  return { ...DEFAULT_USER_PREFERENCES };
}

function writeLocalPrefs(prefs: Required<UserPreferences>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * Persistent user preferences with localStorage.
 * Server sync via API will be added when a /api/preferences endpoint exists.
 */
export function useUserPreferences(_context: { userId: string } | null): UseUserPreferencesReturn {
  const [preferences, setPreferences] = useState<Required<UserPreferences>>(readLocalPrefs);

  const updatePreferences = useCallback((patch: Partial<UserPreferences>) => {
    setPreferences((prev: Required<UserPreferences>) => {
      const next = { ...prev, ...patch };
      writeLocalPrefs(next);
      return next;
    });
  }, []);

  return { preferences, loading: false, updatePreferences };
}
